import {
  createSlice,
  PayloadAction,
  Slice,
  CaseReducerActions,
  SliceCaseReducers,
  AnyAction,
} from "@reduxjs/toolkit";
import { Reducer } from "react";
import { RootState, AppThunk } from ".";
import {
  SessionService,
  ISessionService,
} from "../services/proyecta/session.service";
import { ILang, UserDTO } from "../models";
import {
  LocalStorageHelper,
  Enums,
  Utilities,
  Dictionary,
  IDictionary,
} from "../utils/utilities";

export interface ISessionState {
  lang: ILang;
  user: UserDTO;
  langs: ILang[];
  params: any;
  labels: IDictionary<string, string>;
  currentThreads?: number;
  completedThreads?: number;
  isLoadingContainer?: boolean;
  progress?: number;
  pendingLoad?: number;
}

export class SessionStore {
  reducer: Reducer<any, AnyAction>;
  service: ISessionService;
  initialState: ISessionState;
  slice: Slice;
  actions: CaseReducerActions<SliceCaseReducers<any>>;

  selectLang: any;
  selectUser: any;
  selectLangs: any;
  selectParams: any;
  selectLabels: any;
  selectCurrentThreads: any;
  selectCompletedThreads: any;
  selectLoadingContainer: any;
  selectProgress: any;
  selectPendingLoad: any;
  constructor() {
    this.service = new SessionService();
    const sessionlang = LocalStorageHelper.get<ILang>(
      Enums.LocalStorageKeys.currentLang
    );
    this.initialState = {
      lang: Utilities.isNullObject(sessionlang) ? null : sessionlang,
      user: new UserDTO(),
      langs: [],
      params: [],
      labels: new Dictionary<string, string>(),
      currentThreads: 0,
      completedThreads: 0,
      isLoadingContainer: true,
      progress: 1,
      pendingLoad: 1,
    };
    this.slice = createSlice({
      name: "session",
      initialState: this.initialState,
      reducers: {
        setLang: (state: ISessionState, action: PayloadAction<ILang>) => {
          state.lang = action.payload;
        },
        getLangs: (state: ISessionState, action: PayloadAction<ILang[]>) => {
          state.langs = action.payload;
        },
        getCurrentUser: (
          state: ISessionState,
          action: PayloadAction<UserDTO>
        ) => {
          state.user = action.payload;
        },
        getLabels: (
          state: ISessionState,
          action: PayloadAction<Dictionary<string, string>>
        ) => {
          state.labels = action.payload;
        },
        getParams: (state: ISessionState, action: PayloadAction<any[]>) => {
          state.params = action.payload;
        },
        addThread: (state: ISessionState) => {
          state.currentThreads += 1;
        },
        addCompleteThread: (state: ISessionState) => {
          state.completedThreads += 1;
        },
        setCurrentThreads: (
          state: ISessionState,
          action: PayloadAction<number>
        ) => {
          state.currentThreads = action.payload;
        },
        setCompletedThreads: (
          state: ISessionState,
          action: PayloadAction<number>
        ) => {
          state.completedThreads = action.payload;
        },
        setLoadingContainer: (
          state: ISessionState,
          action: PayloadAction<boolean>
        ) => {
          state.isLoadingContainer = action.payload;
        },
        setProgressLoading: (
          state: ISessionState,
          action: PayloadAction<number>
        ) => {
          state.progress = action.payload;
        },
        setPendingLoad: (
          state: ISessionState,
          action: PayloadAction<number>
        ) => {
          state.pendingLoad = action.payload;
        },
      },
    });
    this.actions = this.slice.actions;
    this.reducer = this.slice.reducer;
    this.selectLang = (state: RootState) => state.session.lang;
    this.selectUser = (state: RootState) => state.session.user;
    this.selectLangs = (state: RootState) => state.session.langs;
    this.selectParams = (state: RootState) => state.session.params;
    this.selectLabels = (state: RootState) => state.session.labels;
    this.selectCurrentThreads = (state: RootState) =>
      state.session.currentThreads;
    this.selectCompletedThreads = (state: RootState) =>
      state.session.completedThreads;
    this.selectLoadingContainer = (state: RootState) =>
      state.session.isLoadingContainer;
    this.selectProgress = (state: RootState) => state.session.progress;
    this.selectPendingLoad = (state: RootState) => state.session.pendingLoad;
  }

  public setLang = (lang: ILang): AppThunk => (dispatch) => {
    LocalStorageHelper.set<ILang>(Enums.LocalStorageKeys.currentLang, lang);
    dispatch(this.actions.setLang(lang));
    dispatch(this.actions.addCompleteThread(null));
    document.location.reload();
  };
  public getLangs = (): AppThunk => (dispatch) => {
    this.service
      .getLangs()
      .then((langs: ILang[]) => {
        dispatch(this.actions.getLangs(langs));
      })
      .catch((err) => Utilities.writeInConsoleLog(err));
  };
  public getLabels = (): AppThunk => (dispatch, getState) => {
    const lang = getState().session.lang;
    if (!Utilities.isNullObject(lang)) {
      this.service
        .getLabels(lang.id)
        .then((labels: Dictionary<string, string>) => {
          dispatch(this.actions.getLabels(labels));
        })
        .catch((err) => Utilities.writeInConsoleLog(err));
    }
  };
  public getParams = (): AppThunk => (dispatch) => {
    this.service
      .getParams()
      .then((params: any[]) => {
        dispatch(this.actions.getParams(params));
      })
      .catch((err) => Utilities.writeInConsoleLog(err));
  };
}
